import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from 'lib/components';
import { Box, Button, Dialog, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { IcDialogClose } from 'res/icons';
import StatusTabPanel from './StatusTabPanel';
import InformationTabPanel from './InformationTabPanel';
import ColorPicker from '../Scheduler/WorkDialog/ColorPicker';
import Order from 'models/Order';
import { TbPlayerTrackNextFilled, TbPlayerTrackPrevFilled } from 'react-icons/tb';
import { useOrdersState, useSearchState } from 'store/selectors';
import PaginationButtons from '../pagination-buttons';
import { OrdersTab } from 'models/OrdersTab';
import { useAppDispatch } from 'store';
import searchAsyncActions from 'store/actions/search.action';
import { useParams } from 'react-router';
import FilterMatchMode from 'models/FilterMatchMode';
import FilterCondition from 'models/FilterCondition';
import FilterMode from 'models/FilterMode';
import FilterField from 'models/FilterField';
import MyTooltip from 'lib/components/utils/tooltip';
import { ordersActions } from 'store/slices/orders.slice';

interface WorkOrderItemDialogProps {
	open: boolean;
	// workOrder: Order;
	isLoadingProp: boolean;
	currentPage: number;
	nextClick: () => void;
	prevClick: () => void;
	current: number;
	onClose: () => void;
}

const useWorkDialogStyles = makeStyles({
	root: {
		'& .MuiPaper-root': {
			backgroundColor: '#f1f1f1',
		},
	},
	header: {
		padding: '15px 10px',
		position: 'relative',
		'& > .title': {
			textAlign: 'center',
			color: '#0859a4',
			fontWeight: '600',
		},
		'& > .icon-buttons': {
			position: 'absolute',
			top: '50%',
			transform: 'translateY(-50%)',
			right: 5,
			padding: 0,
			display: 'flex',
			'& > .MuiIconButton-root': {
				height: 20,
				width: 20,
				padding: 0,
				marginRight: 5,
			},
		},
	},
	body: {
		paddingTop: 15,
		padding: '15px 0',
		'& > .tabs': {
			borderBottom: '0.5px solid #dddfe2',
			minHeight: 0,
			'& .MuiTab-root': {
				fontSize: 14,
				padding: '3px 5px',
				textTransform: 'none',
				minHeight: 0,
				minWidth: 0,
				'&[aria-selected="true"]': {
					color: '#0859a4',
				},
			},
		},
	},
	select: {
		borderRadius: 0,
		fontSize: 12,
		'& li': {
			fontSize: 12,
		},
	},
	footer: {
		padding: '15px 10px',
		'& .warning': {
			marginLeft: 10,
			color: '#fa2e2e',
		},
		'& > .button': {
			margin: '10px 10px 0 0',
			borderRadius: 0,
			color: '#0859a4',
			textTransform: 'none',
			'&:hover': {
				backgroundColor: '#0859a4',
				color: '#ffffff',
			},
		},
	},
});

const WorkOrderItemDialog: React.FC<WorkOrderItemDialogProps> = ({
	currentPage,
	onClose,
	open,
	isLoadingProp,
	nextClick,
	prevClick,
	current,
}) => {
	const classes = useWorkDialogStyles();

	const [value, setValue] = useState(0);
	const [color, setColor] = useState('#FFF000');

	const [colorPickerOpen, setColorPickerOpen] = useState(false);

	// const [currentPage, setCurrentPage] = React.useState(1);
	const [currentOrder, setCurrentOrder] = React.useState(1);

	const ordersState = useOrdersState();
	const searchState = useSearchState();
	const dispatch = useAppDispatch();

	const { currentWorkOrder: workOrder } = useOrdersState();

	// const [startTime, setStartTime] = useState<Date | null>(new Date());
	// const [endTime, setEndTime] = useState<Date | null>(new Date());

	// React.useEffect(()=>{
	// 	console.log("orders are:", orders);
	// },[])
	// const currentIndex = orders.findIndex(order => order.orderNo === workOrder.orderNo);
	// const currentIndex = 0;

	// const currentIndex = ordersState.actualList.findIndex((order) => order.orderNo === workOrder.orderNo);

	const currentIndex = workOrder
		? ordersState.actualList.findIndex((order) => order.orderNo === workOrder.orderNo)
		: -1;

	useEffect(() => {
		if (open && workOrder) {
			const idx = ordersState.actualList.findIndex((order) => order.orderNo === workOrder.orderNo);
			if (idx !== -1) {
				setCurrentOrder(idx + 1);
			}
		}
	}, [workOrder, open, ordersState.actualList]);

	const handlePrevClick = () => {
		if (currentIndex > 0) {
			dispatch(ordersActions.setCurrentWorkOrder(ordersState.actualList[currentIndex - 1]));
			setCurrentOrder((prev) => prev - 1);
		}
		console.log('prev clicked');
	};

	const handleNextClick = () => {
		if (currentIndex < ordersState.actualList.length - 1) {
			dispatch(ordersActions.setCurrentWorkOrder(ordersState.actualList[currentIndex + 1]));
			setCurrentOrder((prev) => prev + 1);
		}
	};

	const handleSkipToStart = () => {
		if (ordersState.actualList.length > 0) {
			dispatch(ordersActions.setCurrentWorkOrder(ordersState.actualList[0]));
			setCurrentOrder(1);
		}
	};

	const handleSkipToEnd = () => {
		if (ordersState.actualList.length > 0) {
			dispatch(ordersActions.setCurrentWorkOrder(ordersState.actualList[ordersState.actualList.length - 1]));
			setCurrentOrder(ordersState.actualList.length);
		}
	};

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	return (
		<Dialog className={classes.root} open={open} fullWidth maxWidth="lg">
			<Box className={classes.header}>
				<Typography variant="p" className="title">
					Work Order: #{workOrder?.orderNo}
				</Typography>

				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
					<Box>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								width: '90%',
							}}
						>
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<div
									style={{
										display: 'flex',
										gap: '10px',
										alignItems: 'center',
										fontSize: '14px',
										color: '#0859a4',
									}}
								>
									<div style={{ display: 'flex', width: '8rem' }}>
										Total results:{' '}
										{searchState.list.total === undefined
											? '...........'
											: `${searchState.list.total}`}
									</div>
									<div style={{ display: 'flex', width: '5.5rem' }}>
										Orders: {currentOrder}/{`${Math.ceil(ordersState.actualList.length / 1 || 1)}`}
									</div>
									<div style={{ display: 'flex', width: '5.5rem' }}>
										Page: {currentPage}/{`${Math.ceil(searchState.list.total / 25 || 1)}`}
									</div>
								</div>
								<MyTooltip title="Previous Page">
									<span>
										<Button
											// variant=""
											// style={{ fontSize: '10px' }}
											onClick={() => {
												prevClick();
												setCurrentOrder(1);
											}}
											disabled={isLoadingProp || current <= 0}
											style={{
												color: isLoadingProp || current <= 0 ? '#ddd' : '#0859a4',
												width: 30,
												height: 30,
											}}
										>
											<TbPlayerTrackPrevFilled />
										</Button>
									</span>
								</MyTooltip>

								<PaginationButtons
									handleSkipToStart={handleSkipToStart}
									handlePrevClick={handlePrevClick}
									handleNextClick={handleNextClick}
									handleSkipToEnd={handleSkipToEnd}
									current={currentIndex}
									total={ordersState.actualList.length}
									limit={1}
								/>
								<MyTooltip title="Next Page">
									<span>
										<Button
											color="secondary"
											// variant="contained"
											// style={{ fontSize: '10px' }}
											onClick={() => {
												nextClick();
												setCurrentOrder(1);
											}}
											disabled={isLoadingProp || searchState.list.total <= current + 25}
											style={{
												color:
													isLoadingProp || searchState.list.total - 1 <= current + 25
														? '#ddd'
														: '#0859a4',
												width: 30,
												height: 30,
											}}
										>
											<TbPlayerTrackNextFilled />
										</Button>
									</span>
								</MyTooltip>
							</div>
						</div>
					</Box>
					<Box className="icon-buttons">
						<IconButton onClick={onClose}>
							<IcDialogClose />
						</IconButton>
					</Box>
				</div>
			</Box>
			<Box className={classes.body}>
				<Tabs value={value} className="tabs" onChange={handleChange} centered>
					<Tab label="Status" />
					<Tab label="Information" />
				</Tabs>
				<StatusTabPanel onClose={onClose} index={0} workOrder={workOrder!} value={value} />
				<InformationTabPanel value={value} index={1} workOrder={workOrder!} onClose={onClose} />
			</Box>
			<ColorPicker
				color={color}
				setColor={setColor}
				open={colorPickerOpen}
				onClose={() => setColorPickerOpen(false)}
			></ColorPicker>
		</Dialog>
	);
};

export default WorkOrderItemDialog;
